import React from 'react';
import { toast } from 'react-toastify';
import { jsx } from '@emotion/core';
import theme from '../../theme/theme';
import InsuranceTypesTable from './components/InsuranceTypesTable';
import InsuranceTypeModal from './components/InsuranceTypeModal';
import {
  useInsurance_TypeQuery,
  useDelete_Insurance_TypeMutation,
} from '../../generated/graphql';
import { InsuranceType } from '../../utils/types';
import UpdateHandlers from '../../utils/handlers';
import Confirm from '../../components/confirm/Confirm';
import Toastify from '../../components/toastify/Toastify';
import { useAuthState } from '../../context/auth';
import SectionTitle from '../../components/sectionTitle/SectionTitle';
import { ADMIN } from '../../routes/routes';
/**@jsx jsx*/

const InsuranceTypees: React.FC = () => {
  const { userId } = useAuthState();
  const [isOpen, setIsOpen] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [selectedInsuranceType, setSelectedInsuranceType] = React.useState<
    number | undefined
  >();
  const { data: insuranceTypes, loading } = useInsurance_TypeQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [deleteInsuranceType] = useDelete_Insurance_TypeMutation();

  const close = React.useCallback(() => {
    setIsOpen(false);
    setSelectedInsuranceType(undefined);
    setShowConfirm(false);
  }, []);

  const edit = (insuranceType: InsuranceType) => {
    setIsOpen(true);
    setSelectedInsuranceType(insuranceType.id);
  };

  const notify = (success: boolean, message: string) => {
    toast(<Toastify {...{ success, message }} />, {
      position: 'top-right',
      autoClose: 7000,
      progressStyle: {
        background: success ? theme.colors.green[200] : theme.colors.red[200],
      },
    });
  };

  const remove = async () => {
    try {
      const response = await deleteInsuranceType({
        variables: {
          id: selectedInsuranceType,
          doneBy: userId,
        },
        update: UpdateHandlers.deleteInsuranceType,
      });
      setShowConfirm(false);

      if (response.data?.update_insurance_type?.affected_rows) {
        notify(true, 'Insurance Type deleted successfully.');
      } else {
        notify(false, 'failed to delete insurance type.');
      }
    } catch (err) {}
  };

  const confirm = (insuranceType: InsuranceType) => {
    setSelectedInsuranceType(insuranceType.id);
    setShowConfirm(true);
  };

  const getInsuranceType = (id?: number) => {
    if (id) {
      const insuranceType = insuranceTypes?.insurance_type?.find(
        (insuranceType) => insuranceType.id === id
      );
      if (insuranceType) {
        return {
          id: insuranceType.id,
          name: insuranceType.name,
        };
      }
    }
    return;
  };

  const getInsuranceTypes = (): InsuranceType[] => {
    return (
      insuranceTypes?.insurance_type?.map((insuranceType) => ({
        id: insuranceType.id,
        name: insuranceType.name,
        createdAt: insuranceType.created_at,
        lastChanged: insuranceType.updated_at,
      })) || []
    );
  };

  return (
    <React.Fragment>
      {isOpen && (
        <InsuranceTypeModal
          onClose={close}
          insuranceType={getInsuranceType(selectedInsuranceType)}
        />
      )}

      {showConfirm && (
        <Confirm
          cancel={{ label: 'cancel', function: close }}
          confirm={{ label: 'ok', function: remove }}
          onClose={close}
          title="Delete Insurance Type"
          message="Are you sure you want to delete?"
        />
      )}

      <SectionTitle
        title="Insurance Type"
        action={{
          label: 'Add',
          icon: { name: 'plus', color: theme.colors.white, position: 'after' },
          function: () => {
            setIsOpen(true);
          },
          allowed: [ADMIN],
        }}></SectionTitle>
      <InsuranceTypesTable
        isLoading={loading}
        insuranceTypes={getInsuranceTypes()}
        actions={[
          { label: 'Delete', action: confirm },
          { label: 'Edit', action: edit },
        ]}
      />
    </React.Fragment>
  );
};

export default InsuranceTypees;
